import Seo from "@/components/Seo";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceTypes = [
  "Plumbing",
  "Electrical",
  "Roofing",
  "Tiling",
  "Carpentry",
  "Painting",
  "Remodeling",
  "Cleaning",
  "Lawn Care",
  "Pool Maintenance",
  "HVAC",
  "Pest Control",
  "Janitorial",
  "Window Cleaning",
  "Project Payment Management",
  "Other (Please describe)",
];

const serviceAreas = [
  "Saint Lucia",
  "Antigua and Barbuda",
  "Commonwealth of Dominica",
  "Grenada",
  "Montserrat",
  "Saint Kitts and Nevis",
  "Saint Vincent and the Grenadines",
  "Barbados",
];

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    message: "",
  });

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const svc = searchParams.get("service");
    const msg = searchParams.get("message");
    if (svc || msg) {
      setFormData((prev) => ({
        ...prev,
        ...(svc ? { service_type: svc } : {}),
        ...(msg ? { message: msg } : {}),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone || !formData.service_type || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Your message has been sent successfully!");
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      <Seo
        title="Contact Us"
        description="Get in touch with ProToCall for handyman services across the Caribbean. Call +1 758 723 0202 or email info@cariphs.com."
        path="/contact"
      />
      {/* Hero Section */}
      <section className="bg-slate-900 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <span className="inline-block bg-orange-500/20 text-orange-400 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
              Contact Us
            </span>
            <h1
              className="text-4xl md:text-5xl font-bold text-white mb-6"
              style={{ fontFamily: "Manrope, sans-serif" }}
              data-testid="contact-page-title"
            >
              Get a Free Quote
            </h1>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              Have a question or need a quote? We're here to help. Fill out the form
              below and we'll get back to you within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <h2
                className="text-2xl font-bold text-slate-900 mb-6"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Get in Touch
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Phone</h3>
                    <a
                      href="tel:+17587230202"
                      className="text-slate-600 hover:text-orange-500 transition-colors"
                    >
                      +17587230202
                    </a>
                    <p className="text-sm text-slate-500 mt-1">Mon-Fri 8am-6pm</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Email</h3>
                    <a
                      href="mailto:info@cariphs.com"
                      className="text-slate-600 hover:text-orange-500 transition-colors"
                    >
                      info@cariphs.com
                    </a>
                    <p className="text-sm text-slate-500 mt-1">We respond within 24 hours</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Location</h3>
                    <p className="text-slate-600">
                      Saint Lucia, Caribbean
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Business Hours</h3>
                    <p className="text-slate-600">
                      Monday - Friday: 8am - 6pm<br />
                      Saturday: 9am - 4pm<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>

              {/* Service Areas */}
              <div className="mt-10 pt-10 border-t border-slate-200">
                <h3
                  className="text-lg font-semibold text-slate-900 mb-4"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Service Areas
                </h3>
                <div className="flex flex-wrap gap-2">
                  {serviceAreas.map((area) => (
                    <span
                      key={area}
                      className={`px-3 py-1 text-sm rounded-full ${
                        area === "Saint Lucia"
                          ? "bg-orange-500 text-white font-semibold"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {area}
                      {area === "Saint Lucia" ? " (Now serving)" : ""}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-slate-500 mt-4">
                  Serving Saint Lucia now, with the wider OECS and Barbados launching soon. Contact us to check availability in your location.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-white border border-slate-200 shadow-lg">
                <CardHeader>
                  <CardTitle
                    className="text-xl font-semibold text-slate-900"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    Request a Quote
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {submitted ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      className="text-center py-12"
                    >
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-green-500" />
                      </div>
                      <h2
                        className="text-2xl font-bold text-slate-900 mb-2"
                        style={{ fontFamily: "Manrope, sans-serif" }}
                      >
                        Message Sent!
                      </h2>
                      <p className="text-slate-600 mb-6">
                        Thank you for reaching out. We'll get back to you within 24 hours.
                      </p>
                      <Button
                        onClick={() => {
                          setSubmitted(false);
                          setFormData({
                            name: "",
                            email: "",
                            phone: "",
                            service_type: "",
                            message: "",
                          });
                        }}
                        variant="outline"
                        className="border-2 border-slate-900"
                        data-testid="send-another-btn"
                      >
                        Send Another Message
                      </Button>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="name" className="text-slate-700 mb-2 block">
                            Full Name *
                          </Label>
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => handleInputChange("name", e.target.value)}
                            placeholder="John Doe"
                            className="h-12"
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-slate-700 mb-2 block">
                            Email Address *
                          </Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange("email", e.target.value)}
                            placeholder="john@example.com"
                            className="h-12"
                            data-testid="contact-email-input"
                          />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="phone" className="text-slate-700 mb-2 block">
                            Phone Number *
                          </Label>
                          <Input
                            id="phone"
                            value={formData.phone}
                            onChange={(e) => handleInputChange("phone", e.target.value)}
                            placeholder="(555) 123-4567"
                            className="h-12"
                            data-testid="contact-phone-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="service" className="text-slate-700 mb-2 block">
                            Service Type *
                          </Label>
                          <Select
                            value={formData.service_type}
                            onValueChange={(value) => {
                              if (value) handleInputChange("service_type", value);
                            }}
                          >
                            <SelectTrigger className="h-12" data-testid="service-type-select">
                              <span className={formData.service_type ? "" : "text-muted-foreground"}>
                                {formData.service_type || "Select a service"}
                              </span>
                            </SelectTrigger>
                            <SelectContent>
                              {serviceTypes.map((type) => (
                                <SelectItem key={type} value={type}>
                                  {type}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="message" className="text-slate-700 mb-2 block">
                          Message *
                        </Label>
                        <Textarea
                          id="message"
                          value={formData.message}
                          onChange={(e) => handleInputChange("message", e.target.value)}
                          placeholder="Please describe your project or the service you need..."
                          className="min-h-[150px]"
                          data-testid="contact-message-input"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold h-12"
                        data-testid="contact-submit-btn"
                      >
                        {loading ? (
                          <>
                            <span className="animate-spin mr-2">⌛</span>
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="mr-2 w-4 h-4" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl font-bold text-slate-900 mb-6 text-center"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Our Service Area
          </h2>
          <div className="aspect-[16/6] bg-slate-100 rounded-xl flex items-center justify-center border border-slate-200">
            <div className="text-center">
              <MapPin className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500">
                Now serving Saint Lucia — OECS &amp; Barbados coming soon
              </p>
              <p className="text-sm text-slate-400 mt-1">
                Saint Lucia • Antigua &amp; Barbuda • Dominica • Grenada • Montserrat • St. Kitts &amp; Nevis • St. Vincent • Barbados
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
